import { ErrorMessage, Formik } from "formik";
import React, { FC, useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import * as _ from "lodash";
import { Typeahead } from "react-bootstrap-typeahead";
import { B2Button, B2Form, B2Input, B2Select } from "@hdb/biome-2";
import "./filter-form.scss";
import Select from "react-select";

export interface Control {
  name: string;
  label?: string;
  placeholder: string;
  type:
  | string
  | "text"
  | "textarea"
  | "number"
  | "checkbox"
  | "date"
  | "datetime"
  | "email"
  | "select"
  | "typeahead"
  | "searchable-select"
  | "month"
  | "password";
  required?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
  options?: { value: any; label: any }[];
  onChange?: (selectedValue: any) => void;
  onInputChange?: (selectedValue: any) => void;
  minDate?: string;
  maxDate?: string;
  isLoading: boolean;
  defaultTime?: string;
  hideVisibility?: boolean;
  selectOptions?: string[];
  selectType?: string | "single" | "multiple";
  resetSearchableSelect?: any;
  hideSelectPlaceholder?: boolean;
}

export interface ControlGroup {
  controlGroup: Control[];
}

export interface FilterFormConfig {
  controls: ControlGroup[];
}

interface FilterFormComponentProps {
  formRef: any;
  formConfig: FilterFormConfig;
  initialValues: any;
  actionConfig: any;
  resetButton?: any;
  validation?: any;
  filterFormMutationFunction?: any;
  filterUpdateCount?: any;
}

const FilterFormComponent: FC<FilterFormComponentProps> = ({
  formRef,
  formConfig,
  initialValues,
  actionConfig,
  resetButton,
  validation,
  filterFormMutationFunction,
  filterUpdateCount,
}) => {
  const [selections, setSelections] = useState<any>([]);
  const typeaheadRef = useRef<any>(null);
  const [formikFormConfig, setFormikFormConfig] = useState(null as any);
  const [controls, setControls] = useState(null as any);

  useEffect(() => {
    const copiedFormConfig = _.cloneDeep(formConfig);
    setFormikFormConfig(copiedFormConfig);
  }, [formConfig]);

  useEffect(() => {
    const copiedFormikFormConfig = _.cloneDeep(formikFormConfig);
    if (!copiedFormikFormConfig) return;
    // for select
    if (
      Object.keys(initialValues).length &&
      Object.keys(copiedFormikFormConfig).length
    ) {
      copiedFormikFormConfig.controls.filter((control: any) => {
        if (control?.controlGroup?.length) {
          for (let ctrl of control?.controlGroup ?? []) {
            if (ctrl?.type === "select") {
              for (let option of ctrl?.options ?? []) {
                if (ctrl?.options[0].label !== ctrl?.placeholder) {
                  option.value =
                    typeof option.value === "number"
                      ? option.value.toString()
                      : option.value;
                }
              }
            }
          }
        }
      });
    }
    const { controls } = copiedFormikFormConfig;
    setControls(controls);
  }, [initialValues, formikFormConfig]);

  const addFirstSelectOption = (options: any, placeholder: any) => {
    const copiedOptions = _.cloneDeep(options);

    if (copiedOptions?.length > 0 && copiedOptions[0].label !== placeholder) {
      copiedOptions.unshift({ label: placeholder, value: "" });
    }

    return copiedOptions?.length > 0
      ? copiedOptions
      : [{ label: placeholder || "Select", value: "" }];
  };

  function getDefaultDateTimeValue(defaultTime: string): string {
    const now = new Date();

    try {
      const [time, modifier] = defaultTime.split(" ");
      let [hours, minutes] = time.split(":").map(Number);

      if (modifier === "PM" && hours < 12) hours += 12;
      if (modifier === "AM" && hours === 12) hours = 0;

      now.setHours(hours, minutes, 0, 0); // Set to the desired time

      const offset = now.getTimezoneOffset(); // in minutes
      const local = new Date(now.getTime() - offset * 60 * 1000);

      return local.toISOString().slice(0, 16); // 'YYYY-MM-DDTHH:MM'
    } catch (error) {
      console.error("Invalid defaultTime format:", defaultTime);
      return "";
    }
  }

  const formatDateTimeLocal = (date: Date): string =>
    date.toISOString().slice(0, 16); // returns "YYYY-MM-DDTHH:MM"

  const sanitizeNumberInput = (value: string): string => {
    const sanitized = value.replace(/[^0-9.]/g, "").replace(/^(\d*\.\d*)\..+$/, "$1");
    return sanitized;
  };

  return (
    <div
      style={{ zIndex: "1000", position: "relative" }}
      className="filter-form flex flex-col items-end mb-3 "
    >
      {Object.keys(initialValues).length ? (
        <div className="w-100">
          <Formik
            key={filterUpdateCount}
            innerRef={formRef}
            enableReinitialize={true}
            onSubmit={() => { }}
            validationSchema={validation}
            initialValues={initialValues}
          >
            {({
              handleSubmit,
              handleChange,
              setFieldValue,
              setFieldTouched,
              setFieldError,
              values,
              touched,
              errors,
            }) => (
              <B2Form noValidate onSubmit={handleSubmit}>
                <div className="filter-grid">
                  {controls && controls?.length
                    ? controls?.map((c: any, i: any) => (
                      <div key={i} className="d-flex w-100">
                        {c?.controlGroup?.map((control: any) => (
                          <React.Fragment key={control.name}>
                            {(() => {
                              switch (control.type) {
                                case "text":
                                  return (
                                    <div className="flex items-start w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <B2Form.Group controlId="validationFormik01">
                                          <B2Input
                                            type="text"
                                            placeholder={control.placeholder}
                                            name={control.name}
                                            onChange={(e: any) => {
                                              handleChange(e);
                                              setFieldValue(
                                                control?.name,
                                                e.target.value
                                              );
                                            }}
                                            value={values[control?.name]}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "email":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <B2Form.Group controlId="validationFormik01">
                                          <B2Input
                                            type="email"
                                            placeholder={control.placeholder}
                                            name={control.name}
                                            onChange={(e: any) => {
                                              handleChange(e);
                                              setFieldValue(
                                                control?.name,
                                                e.target.value
                                              );
                                            }}
                                            value={values[control?.name]}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "password":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <B2Form.Group controlId="validationFormik01">
                                          <B2Input
                                            type="password"
                                            placeholder={control.placeholder}
                                            name={control.name}
                                            onChange={(e: any) => {
                                              handleChange(e);
                                              setFieldValue(
                                                control?.name,
                                                e.target.value
                                              );
                                            }}
                                            value={values[control?.name]}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "number":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <B2Form.Group controlId="validationFormik01">
                                          <div
                                            className={
                                              control?.className
                                                ? control?.className
                                                : ""
                                            }
                                          >
                                            <B2Input
                                              type="text"
                                              inputMode="numeric"
                                              placeholder={
                                                control.placeholder
                                              }
                                              name={control.name}
                                              onChange={(e: any) => {
                                                const sanitized = sanitizeNumberInput(
                                                  e.target.value
                                                );
                                                if (sanitized !== e.target.value) {
                                                  e.target.value = sanitized;
                                                }
                                                handleChange(e);
                                                if (
                                                  Number(e.target.value) < 0
                                                ) {
                                                  e.target.value = 0;
                                                  setFieldValue(
                                                    control?.name,
                                                    0
                                                  );
                                                  return;
                                                }
                                                setFieldValue(
                                                  control?.name,
                                                  e.target.value
                                                );

                                                if (control.onChange) {
                                                  control.onChange(
                                                    e.target.value
                                                  );
                                                }
                                              }}
                                              value={values[control?.name]}
                                              required={
                                                control.required ?? false
                                              }
                                              readOnly={
                                                control.readOnly ?? false
                                              }
                                              disabled={
                                                control.disabled ?? false
                                              }
                                              isInvalid={
                                                (!!touched[control.name] &&
                                                  !!errors[control.name]) ??
                                                false
                                              }
                                            />
                                          </div>

                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "textarea":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <B2Form.Group controlId="validationFormik01">
                                          <Form.Control
                                            as="textarea"
                                            rows={3}
                                            placeholder={control.placeholder}
                                            name={control.name}
                                            onChange={(e: any) => {
                                              handleChange(e);
                                              setFieldValue(
                                                control?.name,
                                                e.target.value
                                              );
                                            }}
                                            value={values[control?.name]}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "date":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <Form.Group controlId="validationFormik01">
                                          <Form.Control
                                            type="date"
                                            placeholder={control.placeholder}
                                            name={control.name}
                                            // onChange={handleChange}
                                            onChange={(event) => {
                                              handleChange(event);
                                              setFieldTouched(
                                                control.name,
                                                true,
                                                false
                                              );

                                              if (control.onChange) {
                                                control.onChange(
                                                  event.target.value
                                                );
                                              }

                                              // Dynamic validation -> Report Module
                                              if (
                                                control.name === "endDate" &&
                                                new Date(values.startDate) >=
                                                new Date(event.target.value)
                                              ) {
                                                setFieldError(
                                                  control.name,
                                                  ""
                                                );
                                                return;
                                              } else {
                                                setFieldError(
                                                  control.name,
                                                  undefined
                                                );
                                              }
                                            }}
                                            value={values[control?.name]}
                                            min={control?.minDate || ""}
                                            max={control?.maxDate || ""}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "month":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <Form.Group controlId="validationFormik01">
                                          <Form.Control
                                            type="month"
                                            placeholder={control?.placeholder}
                                            name={control?.name}
                                            // onChange={handleChange}
                                            onChange={(event) => {
                                              handleChange(event);
                                              setFieldTouched(
                                                control.name,
                                                true,
                                                false
                                              );

                                              // Dynamic validation -> Report Module
                                              if (
                                                control.name === "endDate" &&
                                                new Date(values.startDate) >=
                                                new Date(event.target.value)
                                              ) {
                                                setFieldError(
                                                  control.name,
                                                  ""
                                                );
                                                return;
                                              } else {
                                                setFieldError(
                                                  control.name,
                                                  undefined
                                                );
                                              }

                                              if (control.onChange) {
                                                control.onChange(
                                                  event.target.value
                                                );
                                              }
                                            }}
                                            value={values[control?.name]}
                                            min={control?.minDate || ""}
                                            max={control.maxDate || ""}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />

                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </Form.Group>
                                      </div>
                                    </div>
                                  );
                                case "datetime":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <Form.Group controlId="validationFormik01">
                                          <Form.Control
                                            type="datetime-local"
                                            placeholder={control.placeholder}
                                            name={control.name}
                                            onChange={(e: any) => {
                                              handleChange(e);
                                              setFieldValue(
                                                control?.name,
                                                e.target.value
                                              );
                                            }}
                                            onFocus={(e: any) => {
                                              if (
                                                !e.target.value &&
                                                control?.defaultTime
                                              ) {
                                                const time =
                                                  getDefaultDateTimeValue(
                                                    control.defaultTime
                                                  );
                                                if (time) {
                                                  e.target.value = time;
                                                  setFieldValue(
                                                    control?.name,
                                                    time
                                                  );
                                                }
                                              }
                                            }}
                                            min={
                                              control?.minDate
                                                ? formatDateTimeLocal(
                                                  new Date(control.minDate)
                                                )
                                                : ""
                                            }
                                            // max={
                                            //   control?.maxDate
                                            //     ? formatDateTimeLocal(
                                            //         new Date(control.maxDate)
                                            //       )
                                            //     : ""
                                            // }
                                            value={values[control?.name]}
                                            required={
                                              control.required ?? false
                                            }
                                            readOnly={
                                              control.readOnly ?? false
                                            }
                                            disabled={
                                              control.disabled ?? false
                                            }
                                            isInvalid={
                                              (!!touched[control.name] &&
                                                !!errors[control.name]) ??
                                              false
                                            }
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </Form.Group>
                                      </div>
                                    </div>
                                  );

                                case "select":
                                  return (
                                    <>
                                      {control?.hideVisibility ? null : (
                                        <div className="flex items-start  w-100">
                                          <div className="w-100">
                                            <div className="filter-label">
                                              {control.label}
                                            </div>
                                            <B2Form.Group controlId="validationFormik01">
                                              {/* New code */}
                                              <div className="flex-grow-1">
                                                <Select
                                                  classNamePrefix="custom-select"
                                                  className={`${touched[control.name] &&
                                                      errors[control.name]
                                                      ? "custom-select--error"
                                                      : touched[
                                                        control.name
                                                      ] &&
                                                        !errors[control.name]
                                                        ? "custom-select--success"
                                                        : ""
                                                    }`}
                                                  name={control.name}
                                                  options={control.options}
                                                  onChange={(
                                                    selectedOption
                                                  ) => {
                                                    // Create a synthetic event for Formik compatibility
                                                    const syntheticEvent = {
                                                      target: {
                                                        name: control.name,
                                                        value:
                                                          selectedOption?.value ||
                                                          "",
                                                      },
                                                    };
                                                    handleChange(
                                                      syntheticEvent
                                                    );

                                                    if (control.onChange) {
                                                      control.onChange(
                                                        selectedOption?.value ||
                                                        ""
                                                      );
                                                    }
                                                  }}
                                                  value={
                                                    (values[control?.name] &&
                                                      control.options?.find(
                                                        (option: any) =>
                                                          option.value ===
                                                          values[
                                                          control?.name
                                                          ]
                                                      )) ||
                                                    null
                                                  }
                                                  placeholder={
                                                    control?.placeholder
                                                  }
                                                  noOptionsMessage={() =>
                                                    control?.placeholder
                                                  }
                                                  isDisabled={
                                                    control.disabled ?? false
                                                  }
                                                  isSearchable={false}
                                                  maxMenuHeight={280}
                                                  menuPlacement="auto"
                                                />
                                              </div>
                                              {/* New code */}

                                              {/* Old code */}
                                              {/* <B2Select
                                                  name={control.name}
                                                  onChange={(
                                                    selectedOption: any,
                                                    event: any
                                                  ) => {
                                                    if (
                                                      control.onChange &&
                                                      selectedOption
                                                    ) {
                                                      control.onChange(
                                                        selectedOption?.value
                                                      );
                                                    }
                                                    setFieldValue(
                                                      control?.name,
                                                      selectedOption?.value
                                                    );
                                                  }}
                                                  value={values[control?.name]}
                                                  required={
                                                    control.required
                                                      ? control.required
                                                      : false
                                                  }
                                                  defaultValue={
                                                    values[control?.name]
                                                  }
                                                  disabled={
                                                    control.disabled ?? false
                                                  }
                                                  isInvalid={
                                                    (!!touched[control.name] &&
                                                      !!errors[control.name]) ??
                                                    false
                                                  }
                                                  optionList={
                                                    // control?.options || []

                                                    !control?.hideSelectPlaceholder
                                                      ? addFirstSelectOption(
                                                          control?.options,
                                                          control?.placeholder
                                                        )
                                                      : control?.options
                                                    // addFirstSelectOption(
                                                    //   control?.options,
                                                    //   control?.placeholder
                                                    // )
                                                  }
                                                /> */}
                                              {/* Old code */}

                                                <ErrorMessage
                                                  name={control.name}
                                                  component="div"
                                                  className="error-message"
                                                />
                                              </B2Form.Group>
                                            </div>
                                          </div>
                                        )}
                                      </>
                                    );
                                  case "typeahead":
                                    return (
                                      <div className="flex items-start  w-100">
                                        <div className="w-100">
                                          <div className="filter-label">
                                            {control.label}
                                          </div>
                                          <B2Form.Group controlId="validationFormik01">
                                            <Typeahead
                                              multiple={
                                                control?.selectType === 
                                                "multiple"
                                              }
                                              id="basic-typeahead"
                                              onChange={(selected) => {
                                                let selectedValue: any = [];
                                                let selectedLabel: any = [];

                                                selected.forEach((option: any) => {
                                                  selectedLabel.push(
                                                    option.label
                                                  );
                                                  selectedValue.push(
                                                    option.value
                                                  );
                                                });

                                                setSelections(selectedLabel);
                                                if (selectedValue.length) {
                                                  control?.selectType ===
                                                  "multiple"
                                                  ? setFieldValue(
                                                    control.name,
                                                    selectedValue
                                                  )
                                                  : setFieldValue(
                                                    control.name,
                                                    selectedValue[0]
                                                  );
                                              }
                                            }}
                                            onInputChange={(text, event) => {
                                              if (control.onInputChange) {
                                                control.onInputChange(text);
                                              }
                                            }}
                                            options={
                                              control?.selectOptions
                                                ? control?.selectOptions
                                                : []
                                            }
                                            placeholder={control?.placeholder}
                                            selected={selections}
                                            ref={typeaheadRef}
                                            disabled={control?.disabled}
                                          />
                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );

                                case "searchable-select":
                                  return (
                                    <div className="flex items-start  w-100">
                                      <div className="w-100">
                                        <div className="filter-label">
                                          {control.label}
                                        </div>
                                        <B2Form.Group
                                          key={control?.resetSearchableSelect}
                                          controlId="validationFormik01"
                                        >
                                          <Select
                                            classNamePrefix="searchable-select"
                                            className={`${touched[control.name] &&
                                                errors[control.name]
                                                ? "searchable-select--error"
                                                : ""
                                              }`}
                                            options={control?.options}
                                            value={
                                              control?.options?.find(
                                                (option: any) =>
                                                  option.value ===
                                                  values[control.name],
                                              ) ?? null
                                            }
                                            isDisabled={control?.disabled}
                                            placeholder={control?.placeholder}
                                            // isClearable={true}
                                            // isLoading={control?.isLoading}
                                            onChange={(
                                              selectedOption: any
                                            ) => {
                                              if (
                                                control.onChange &&
                                                selectedOption
                                              ) {
                                                control.onChange(
                                                  selectedOption?.value
                                                );
                                              }
                                              setFieldValue(
                                                control?.name,
                                                selectedOption?.value
                                              );
                                            }}
                                            required={
                                              control.required ?? false
                                            }
                                            maxMenuHeight={280}
                                          />

                                          <ErrorMessage
                                            name={control.name}
                                            component="div"
                                            className="error-message"
                                          />
                                        </B2Form.Group>
                                      </div>
                                    </div>
                                  );

                                default:
                                  return null;
                              }
                            })()}
                          </React.Fragment>
                        ))}
                      </div>
                    ))
                    : ""}
                </div>
              </B2Form>
            )}
          </Formik>
        </div>
      ) : null}
      <div
        className="mt-3"
        style={{ width: "100%", display: "flex", justifyContent: "flex-start" }}
      >
        {resetButton && (
          <B2Button
            onClick={() => {
              resetButton.event();
              setSelections([]);
              if (typeaheadRef.current) {
                typeaheadRef.current.clear();
              }
            }}
            variant={resetButton.variant}
            style={{
              border: "1px solid 1976D2",
              marginLeft: "1rem",
            }}
          >
            {resetButton.label}
          </B2Button>
        )}
        <B2Button
          onClick={actionConfig.event}
          variant={actionConfig.variant}
          className="ms-2"
        >
          {actionConfig.label}
        </B2Button>
      </div>
    </div>
  );
};

export default FilterFormComponent;
